import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useThreadStore } from '../stores/threadStore'
import ConfirmDialog from '../components/ConfirmDialog'

function daysUntilPurge(deletedAt: string | null): number {
  if (!deletedAt) return 30
  const deleted = new Date(deletedAt).getTime()
  const purgeAt = deleted + 30 * 24 * 60 * 60 * 1000
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000)))
}

export default function Trash() {
  const navigate = useNavigate()
  const { trashedThreads, error, fetchTrash, restoreThread, permanentDeleteThread } = useThreadStore()
  const [confirmId, setConfirmId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTrash().finally(() => setLoading(false))
  }, [fetchTrash])

  const handlePermanentDelete = async () => {
    if (confirmId === null) return
    await permanentDeleteThread(confirmId)
    setConfirmId(null)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b border-elevated shrink-0">
        <button onClick={() => navigate('/research')} aria-label="Back to Research" className="text-text-secondary hover:text-text-primary">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h1 className="font-medium text-lg">Trash</h1>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : trashedThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg className="w-12 h-12 text-text-muted opacity-30 mb-3" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            <p className="text-text-secondary text-sm">
              {error || 'Trash is empty'}
            </p>
            {error && (
              <button
                onClick={() => { setLoading(true); fetchTrash().finally(() => setLoading(false)) }}
                className="mt-3 px-4 py-2 rounded-lg bg-elevated text-text-secondary text-sm font-medium hover:bg-bg transition-colors"
              >
                Try again
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-text-muted mb-3">
              Threads are permanently deleted after 30 days.
            </p>
            {trashedThreads.map((thread) => {
              const days = daysUntilPurge(thread.deleted_at)
              return (
                <div key={thread.id} className="bg-surface rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{thread.title}</p>
                      <p className="text-xs text-text-muted mt-0.5">
                        {days > 0 ? `Auto-deletes in ${days} day${days !== 1 ? 's' : ''}` : 'Scheduled for deletion'}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => restoreThread(thread.id)}
                        className="text-accent text-xs font-medium px-3 py-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 transition-colors"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => setConfirmId(thread.id)}
                        className="text-error/70 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-error/10 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmId !== null}
        title="Permanently delete?"
        message="This thread and all its messages will be gone forever. This cannot be undone."
        confirmLabel="Delete Forever"
        destructive
        onConfirm={handlePermanentDelete}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  )
}
